import * as React from 'react';
import * as Sentry from '@sentry/react';
import { CreateBase, useNotify, useRefresh } from 'react-admin';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton as MuiIconButton,
  Box,
  Button,
  FormControl,
  Select,
  FormHelperText,
  CircularProgress,
  MenuItem} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { TextField as MuiTextField } from '@mui/material';
import CategoryService from '../../services/category/categoryService';
import { useParams } from 'react-router-dom';
import { getBackendLanguage, getLanguageOptions } from '../../core/i18n';
import { labelAboveSx } from '../../core/theme/formStyles';
import { dialogActionsSx, dialogButtonSx } from '../../core/theme/buttonStyles';

const inputSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '6px',
    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--color-secondary)' },
    '& .MuiSelect-outlined': {
      borderRadius: '6px',
      height: '1.4375em',
      '& .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--color-secondary)' },
    },
  },
  '& .MuiInputBase-input': { fontSize: 14, padding: '10px 12px' },
};

type Props = { open: boolean; onClose: () => void };

export const CategoryAddDialog: React.FC<Props> = ({ open, onClose }) => {
  const categoryService = new CategoryService();
  const notify = useNotify();
  const refresh = useRefresh();
  const { id } = useParams();

  const initialValues = {
    name: '',
    // Default the language select to the language currently selected in the UI.
    language: getBackendLanguage(),
  };

  const { t } = useTranslation();

  const validationSchema = Yup.object({
    name: Yup.string().required(t('validation.nameRequired')),
    language: Yup.string().required(t('validation.languageRequired')),
  });

  return (
    <CreateBase resource="commissionManagement">
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="lg"
        slotProps={{
          backdrop: { sx: { backgroundColor: 'rgba(0,0,0,0.5)' } },
          paper: { sx: { borderRadius: 2, border: '1px solid var(--color-secondary)' } },
        }}
        sx={{
          padding: 0,
          maxWidth: 700,
          mx: 'auto',
          width: '100%',
        }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          {t('locationItems.dialog.addCategory')}
          <MuiIconButton
            aria-label="close"
            onClick={onClose}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </MuiIconButton>
        </DialogTitle>

        <DialogContent dividers>
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={async (values) => {
              try {
                const payload = {
                  name: values.name,
                  locationId: id,
                };
                console.log(payload);
                await categoryService.createCategory(payload);

                notify(t('common.saved'), { type: 'success' });
                refresh();
                onClose();
              } catch (error: any) {
                notify(error.response.data.error, { type: 'error' });
                console.error(error.response.data.error);
                Sentry.captureException(error);
              }
            }}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              handleSubmit,
              isSubmitting,
            }) => (
              <Form onSubmit={handleSubmit}>
                <Box sx={{ display: 'flex', flexDirection: 'row', gap: 3, width: '100%', ...labelAboveSx }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                      <FormControl
                        size="small"
                        fullWidth
                        error={touched.language && Boolean(errors.language)}
                      >
                        <Select
                          displayEmpty
                          name="language"
                          value={values.language}
                          onBlur={handleBlur}
                          onChange={handleChange}
                        >
                          <MenuItem value="" disabled>
                            {t('locationsManagement.create.language')}
                          </MenuItem>
                          {getLanguageOptions(t).map((c) => (
                            <MenuItem key={c.id} value={c.id}>
                              {c.name}
                            </MenuItem>
                          ))}
                        </Select>

                        <FormHelperText>
                          {touched.language && errors.language ? errors.language : ' '}
                        </FormHelperText>
                      </FormControl>
                      <MuiTextField
                        name="name"
                        label={t('locationItems.dialog.name')}
                        sx={inputSx}
                        size="small"
                        fullWidth
                        value={values.name}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.name && Boolean(errors.name)}
                        helperText={touched.name && errors.name}
                      />
                    </Box>
                  </Box>
                </Box>

                <Box sx={dialogActionsSx}>
                  <Button variant="outlined" type="button" onClick={onClose} sx={dialogButtonSx}>
                    {t('common.cancel')}
                  </Button>

                  <Button
                    type="submit"
                    variant="contained"
                    disabled={isSubmitting}
                    sx={dialogButtonSx}
                  >
                    {isSubmitting ? (
                      <CircularProgress size={20} sx={{ color: 'var(--color-on-primary)' }} />
                    ) : (
                      t('common.saveChanges')
                    )}
                  </Button>
                </Box>
              </Form>
            )}
          </Formik>
        </DialogContent>
      </Dialog>
    </CreateBase>
  );
};
